/**
 * recoveryMnemonic.ts — BIP39 + HKDF-SHA256 wrap of the account root key.
 *
 * Threat model
 * ------------
 * The server stores only the wrapped (ciphertext) form of the account root
 * key. To unwrap, the user must supply their 12-word mnemonic. The secret
 * material that decrypts the wrap never touches the server.
 *
 * (We considered a Wallet-pass-as-recovery flow alongside the mnemonic and
 *  intentionally cancelled it — Wallet passes are user-shareable assets and
 *  cannot serve as a single-factor secret. iCloud Keychain sync of the
 *  unwrapped key handles transparent multi-device delivery on iOS; the
 *  mnemonic is the universal fallback.)
 *
 * Mnemonic
 * --------
 * 12 words from BIP39 English wordlist — exactly 128 bits of uniformly
 * random entropy by construction (generated via `Crypto.getRandomBytes`,
 * never user-chosen). We feed the 16-byte BIP39 entropy — not the raw word
 * string — into the KDF, so capitalisation / whitespace / unicode form of
 * what the user types is irrelevant as long as the words validate.
 *
 * KDF
 * ---
 * HKDF-SHA256 with:
 *   ikm  = 16-byte BIP39 entropy
 *   salt = 32-byte per-account wrap salt (server-persisted, immutable)
 *   info = `eleplan-snapshot-v2:account-root-wrap`
 *   len  = 32
 *
 * We used to stack Argon2id (t=3, m=64 MiB) on top of this. That was
 * cargo-culting password-grade stretching onto an input that already has
 * 128 bits of random entropy — 2^128 is already well past what any
 * attacker can brute force regardless of KDF choice, and in pure-JS on a
 * phone the Argon2 call was a 20–60 s UI freeze. HKDF is the primitive
 * explicitly designed for "extract and expand from already-random
 * material," runs in sub-millisecond, and gives the same practical
 * security for this input. The per-account salt still prevents cross-
 * account correlation and keeps two accounts with coincidentally-similar
 * mnemonics from sharing a wrap key.
 *
 * This is ALSO the KDF v1 is stamped as — there is no Argon2 legacy path
 * because Argon2 wraps never shipped. If a future version needs
 * password-grade stretching (e.g. a user-chosen PIN wrap), it must use a
 * different `kdf` tag so the dispatch below stays unambiguous.
 *
 * Wrap envelope
 * -------------
 *   wrap_version: 1
 *   kdf: 'hkdf-sha256'
 *   kdf_params: { hash: 'sha256' }  (informational; HKDF has no tunables)
 *   wrap_salt_hex: 32-byte per-account salt (immutable once set)
 *   wrap_iv_hex: 12-byte AES-GCM nonce (random per wrap)
 *   wrap_tag_hex: 16-byte AES-GCM auth tag
 *   wrapped_root_key_hex: AES-GCM ciphertext of the 32-byte root key
 *
 * The envelope is upload-and-overwrite: re-wrapping (e.g. user rotates
 * their mnemonic via mode='rotate-mnemonic') replaces the prior blob; the
 * old blob is unrecoverable after that, which is the desired behavior for
 * "I want a new recovery code." The salt is preserved across rotations.
 */

import * as Crypto from 'expo-crypto';
import * as SecureStore from 'expo-secure-store';
import { gcm } from '@noble/ciphers/aes.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, hexToBytes, utf8ToBytes } from '@noble/ciphers/utils.js';
import { generateMnemonic, mnemonicToEntropy, validateMnemonic } from '@scure/bip39';
// .js suffix is required: @scure/bip39's package "exports" map only
// declares the `.js`-suffixed wordlist subpaths. Bare specifiers without
// the suffix resolve fine at runtime in some bundlers but fail under
// Node's strict ESM resolver and under jest-resolve, which both honor
// "exports".
import { wordlist as english } from '@scure/bip39/wordlists/english.js';

const KEYCHAIN_ACCESSIBLE = SecureStore.WHEN_PASSCODE_SET_THIS_DEVICE_ONLY;
const mnemonicSlot = (accountId: string) => `snap_recovery_mnemonic_${accountId}`;

export const RECOVERY_WRAP_VERSION = 1 as const;

/** Canonical KDF tag stamped into every wrap. */
export const WRAP_KDF = 'hkdf-sha256' as const;
export type WrapKdf = typeof WRAP_KDF;

/**
 * HKDF has no tunable work factor — you either use it or you don't —
 * so `kdf_params` is informational only. We still stamp it so future
 * format evolutions (e.g. a PIN-wrap variant that does use a tunable
 * KDF) can land without breaking the envelope schema.
 */
export const WRAP_KDF_PARAMS = { hash: 'sha256' } as const;
export type WrapKdfParams = { readonly hash: 'sha256' };

/** Domain-separation string for the HKDF expand step. Kept short and
 *  stable — changing it orphans every in-flight wrap. */
const WRAP_INFO = 'eleplan-snapshot-v2:account-root-wrap' as const;

export interface RecoveryWrap {
  wrap_version: typeof RECOVERY_WRAP_VERSION;
  kdf: WrapKdf;
  kdf_params: WrapKdfParams;
  wrap_salt_hex: string;
  wrap_iv_hex: string;
  wrap_tag_hex: string;
  wrapped_root_key_hex: string;
}

/** Generate a new 12-word BIP39 recovery mnemonic (~128 bits of entropy). */
export function generateRecoveryMnemonic(): string {
  return generateMnemonic(english, 128);
}

/**
 * Validate a user-entered mnemonic against BIP39 (wordlist + checksum).
 * Cheap pre-flight check so an obviously typo'd mnemonic fails with a
 * useful error ("that's not a valid recovery code") instead of the
 * generic "decryption failed" you'd see if we let it through to the
 * AES-GCM auth step.
 */
export function isValidRecoveryMnemonic(mnemonic: string): boolean {
  const normalized = normalizeMnemonic(mnemonic);
  if (!normalized) return false;
  try {
    return validateMnemonic(normalized, english);
  } catch {
    return false;
  }
}

/**
 * Lower-case, trim, collapse whitespace. We do this before hashing so the
 * user can capitalise / over-space the mnemonic without breaking unwrap.
 * BIP39's spec already requires NFKD-normalised lowercase, so this is just
 * making the user's input meet the spec.
 */
export function normalizeMnemonic(mnemonic: string): string {
  return String(mnemonic || '')
    .normalize('NFKD')
    .toLowerCase()
    .trim()
    .split(/\s+/u)
    .join(' ');
}

function deriveWrapKey(mnemonic: string, saltHex: string): Uint8Array {
  const normalized = normalizeMnemonic(mnemonic);
  // Feed the BIP39 entropy bytes (16 bytes = 128 bits) into HKDF rather
  // than the raw word string. This gives a stable input regardless of
  // case / whitespace / unicode composition variants the user types, and
  // it's the same 128-bit random material the wordlist is just an
  // encoding of.
  const ikm = mnemonicToEntropy(normalized, english);
  const salt = hexToBytes(saltHex);
  return hkdf(sha256, ikm, salt, utf8ToBytes(WRAP_INFO), 32);
}

/**
 * Wrap (encrypt) the account root key with an HKDF-SHA256 key derived
 * from `mnemonic`. The server-issued `accountSaltHex` is mixed in as the
 * HKDF salt so two accounts that happen to share a mnemonic don't share a
 * wrap key.
 */
export async function wrapAccountRootKey(
  rootKey: Uint8Array,
  mnemonic: string,
  accountSaltHex: string,
): Promise<RecoveryWrap> {
  if (rootKey.length !== 32) throw new Error('Root key must be 32 bytes.');
  const wrapKey = deriveWrapKey(mnemonic, accountSaltHex);
  const ivBuf = await Crypto.getRandomBytesAsync(12);
  const iv = new Uint8Array(ivBuf);
  const cipher = gcm(wrapKey, iv);
  const ct = cipher.encrypt(rootKey);
  return {
    wrap_version: RECOVERY_WRAP_VERSION,
    kdf: WRAP_KDF,
    kdf_params: { ...WRAP_KDF_PARAMS },
    wrap_salt_hex: accountSaltHex,
    wrap_iv_hex: bytesToHex(iv),
    wrap_tag_hex: bytesToHex(ct.slice(-16)),
    wrapped_root_key_hex: bytesToHex(ct.slice(0, -16)),
  };
}

/**
 * Unwrap with the user-supplied mnemonic. Returns null on:
 *   - malformed envelope
 *   - unsupported `kdf` tag (we refuse to derive against anything we
 *     didn't author — forward compat for future wrap formats, and
 *     protection against a compromised server swapping the algorithm)
 *   - mnemonic that fails GCM auth (wrong words, wrong order)
 * Caller should treat null as "user gave the wrong recovery code" and
 * re-prompt — never log details that would help an attacker distinguish
 * "wrong kdf" from "wrong mnemonic".
 */
export async function unwrapAccountRootKey(
  wrap: RecoveryWrap,
  mnemonic: string,
): Promise<Uint8Array | null> {
  try {
    if (wrap.wrap_version !== RECOVERY_WRAP_VERSION) return null;
    if (wrap.kdf !== WRAP_KDF) return null;
    const wrapKey = deriveWrapKey(mnemonic, wrap.wrap_salt_hex);
    const iv = hexToBytes(wrap.wrap_iv_hex);
    const ct = hexToBytes(wrap.wrapped_root_key_hex);
    const tag = hexToBytes(wrap.wrap_tag_hex);
    const buf = new Uint8Array(ct.length + tag.length);
    buf.set(ct);
    buf.set(tag, ct.length);
    const plain = gcm(wrapKey, iv).decrypt(buf);
    return plain.length === 32 ? plain : null;
  } catch {
    return null;
  }
}

/**
 * Encode a wrap as a JSON-safe object. Equivalent to JSON.stringify (the
 * envelope is already plain primitives) but kept as a wrapper so we have
 * one obvious place to add field stripping or canonicalisation later.
 */
export function serializeWrap(wrap: RecoveryWrap): string {
  return JSON.stringify(wrap);
}

/**
 * Cached mnemonic entry. `wrap_rev` is null ONLY for legacy cache entries
 * written before the rev counter shipped (we read those as bare strings
 * out of SecureStore and upgrade them opportunistically — see
 * getCachedMnemonic + revealRecoveryMnemonic).
 */
export interface CachedMnemonicEntry {
  mnemonic: string;
  wrap_rev: number | null;
}

const CACHE_ENTRY_VERSION = 1 as const;

/**
 * Cache the mnemonic on this device alongside the wrap revision it was
 * captured at.
 *
 * The cache is per-account, in SecureStore, behind device passcode (no
 * iCloud / Google sync). It is intentionally NOT the source of truth —
 * the source of truth is the user having written the mnemonic down. If
 * the cache is wiped (uninstall, sign out, manual reset), the user can
 * still recover by typing the words; they just can't view them on this
 * device anymore.
 *
 * `wrapRev` is the server-assigned revision counter at the time of
 * capture. Reveal compares it against the current server rev and
 * refuses to surface a mnemonic whose rev is behind — those words no
 * longer unlock the live wrap and writing them down would cause
 * permanent data loss if the user later relied on them.
 */
export async function cacheMnemonicLocally(
  accountId: string,
  mnemonic: string,
  wrapRev: number,
): Promise<void> {
  if (!accountId) return;
  if (!Number.isFinite(wrapRev) || wrapRev < 1) {
    throw new Error('cacheMnemonicLocally requires a positive wrap_rev integer.');
  }
  const entry = JSON.stringify({
    v: CACHE_ENTRY_VERSION,
    mnemonic: normalizeMnemonic(mnemonic),
    wrap_rev: Math.trunc(wrapRev),
  });
  const opts = { keychainAccessible: KEYCHAIN_ACCESSIBLE };
  try {
    await SecureStore.setItemAsync(mnemonicSlot(accountId), entry, opts);
  } catch {
    await SecureStore.deleteItemAsync(mnemonicSlot(accountId)).catch(() => {});
    await SecureStore.setItemAsync(mnemonicSlot(accountId), entry, opts);
  }
}

/**
 * Returns the locally-cached mnemonic entry, or null if unavailable on
 * this device.
 *
 * Transparent migration: entries written before the wrap_rev counter
 * shipped were stored as bare normalized strings. We recognise that
 * shape (non-JSON payload) and return it with `wrap_rev: null` so the
 * reveal path can upgrade or invalidate it based on what the server
 * reports.
 */
export async function getCachedMnemonic(accountId: string): Promise<CachedMnemonicEntry | null> {
  if (!accountId) return null;
  const raw = await SecureStore.getItemAsync(mnemonicSlot(accountId)).catch(() => null);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.v === CACHE_ENTRY_VERSION &&
        typeof parsed.mnemonic === 'string') {
      return {
        mnemonic: parsed.mnemonic,
        wrap_rev: Number.isFinite(parsed.wrap_rev) && parsed.wrap_rev >= 1
          ? Math.trunc(parsed.wrap_rev)
          : null,
      };
    }
    // Parsed fine but not our envelope — could be future-format-from-the-
    // future or just a collision with some other blob. Don't try to
    // salvage; treat as missing.
    return null;
  } catch {
    // JSON.parse threw → legacy bare-string cache from before this
    // feature. Safe to treat as "mnemonic known, rev unknown."
    return { mnemonic: raw, wrap_rev: null };
  }
}

/** Forget the locally-cached mnemonic. Recovery still works via user input. */
export async function clearCachedMnemonic(accountId: string): Promise<void> {
  if (!accountId) return;
  await SecureStore.deleteItemAsync(mnemonicSlot(accountId)).catch(() => {});
}

// ─── QR transfer payload ─────────────────────────────────────────────────────
// Device-to-device mnemonic transfer renders a QR on one device and scans it
// on another. The QR encodes the mnemonic behind our own URL scheme so:
//
//   1. The payload is self-describing. Scanning any random QR (a Wi-Fi
//      config, a URL, a vCard) won't accidentally fill the recovery grid —
//      `decodeRecoveryQRPayload` rejects anything that doesn't match.
//   2. If a user ever scans this with the system Camera app, iOS recognises
//      the scheme and offers to open Eleplan. We don't handle the deep link
//      as an automatic fill yet (opt-in scan only), but the scheme is
//      reserved for that future.
//
// Format:
//   eleplan://recovery?m=<percent-encoded normalized 12-word mnemonic>
//
// The mnemonic is always normalized (lowercase, single-spaced) before
// encoding so the QR rendered on any device has the same bytes for the same
// semantic mnemonic — makes cross-device verification easier if we ever add
// hashing-as-integrity. No signing is needed: an attacker swapping a QR
// can't make the user's wrap decrypt anyway; the worst they can do is cause
// a "those 12 words don't form a valid recovery code" error.
const QR_SCHEME = 'eleplan://recovery' as const;

export function encodeRecoveryQRPayload(mnemonic: string): string {
  const normalized = normalizeMnemonic(mnemonic);
  if (!normalized) throw new Error('Cannot encode an empty mnemonic.');
  return `${QR_SCHEME}?m=${encodeURIComponent(normalized)}`;
}

/**
 * Extract a mnemonic from a QR scan. Accepts two shapes for resilience:
 *
 *   1. Our own `eleplan://recovery?m=<encoded>` URL — preferred.
 *   2. A raw 12-word space-separated string, in case a user has a QR from
 *      another tool or an older version of the app.
 *
 * Returns the normalized mnemonic on match, or null on anything that isn't
 * a plausible 12-word BIP39 phrase. The caller must still run
 * `isValidRecoveryMnemonic` before trusting the value — that's where the
 * wordlist + checksum check happens. This function only shape-matches.
 */
export function decodeRecoveryQRPayload(raw: string): string | null {
  if (!raw || typeof raw !== 'string') return null;

  const tryExtract = (candidate: string): string | null => {
    const normalized = normalizeMnemonic(candidate);
    if (!normalized) return null;
    const parts = normalized.split(' ');
    if (parts.length !== 12) return null;
    return normalized;
  };

  const trimmed = raw.trim();

  if (trimmed.toLowerCase().startsWith(QR_SCHEME)) {
    // Use the URL class to parse the query string robustly. We can't parse
    // `eleplan://` with WHATWG URL parser in older RN runtimes, so match on
    // `?m=...` directly.
    const queryIdx = trimmed.indexOf('?');
    if (queryIdx === -1) return null;
    const query = trimmed.slice(queryIdx + 1);
    const pairs = query.split('&');
    for (const pair of pairs) {
      const [key, value] = pair.split('=');
      if (key === 'm' && value) {
        try {
          return tryExtract(decodeURIComponent(value));
        } catch {
          return null;
        }
      }
    }
    return null;
  }

  return tryExtract(trimmed);
}

export function parseWrap(raw: string | RecoveryWrap | null | undefined): RecoveryWrap | null {
  if (!raw) return null;
  try {
    const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!obj || typeof obj !== 'object') return null;
    if (obj.wrap_version !== RECOVERY_WRAP_VERSION) return null;
    if (typeof obj.wrap_salt_hex !== 'string') return null;
    if (typeof obj.wrap_iv_hex !== 'string') return null;
    if (typeof obj.wrap_tag_hex !== 'string') return null;
    if (typeof obj.wrapped_root_key_hex !== 'string') return null;
    return obj as RecoveryWrap;
  } catch {
    return null;
  }
}
